import {
  Calendar, Truck, MapPin, Package 
} from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { usePurchaseOrders } from '@/hooks/use-purchase-orders';
import {
  PO_STATUS_LABELS, PO_STATUS_COLORS 
} from '@/types/orders';

export function DeliverySchedulePage() {
  const {
    data, isLoading 
  } = usePurchaseOrders();

  const orders = data?.data ?? [];
  
  // Filter to orders that need delivery (acknowledged or shipped)
  const deliveryOrders = orders.filter((o) => 
    ['acknowledged', 'shipped', 'sent'].includes(o.status)
  );

  // Group by expected delivery date
  const initialGrouped: Record<string, typeof deliveryOrders> = {};
  const groupedByDate = deliveryOrders.reduce((acc, order) => {
    const date = new Date(order.expectedDeliveryDate).toDateString();
    acc[date] ??= [];
    acc[date].push(order);
    return acc;
  }, initialGrouped);

  const sortedDates = Object.keys(groupedByDate).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD' 
    }).format(amount);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="h-32 animate-pulse bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader />

      {deliveryOrders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No upcoming deliveries</p>
            <p className="text-muted-foreground">
              You have no orders scheduled for delivery
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {sortedDates.map((dateStr) => (
            <div key={dateStr}>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                {formatDate(dateStr)}
              </h2>
              <div className="space-y-3">
                {groupedByDate[dateStr].map((order) => (
                  <Card key={order.poId} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                            <Truck className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold">{order.poNumber}</p>
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${PO_STATUS_COLORS[order.status]}`}>
                                {PO_STATUS_LABELS[order.status]}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {order.deliverySiteName}
                              </span>
                              <span className="flex items-center gap-1">
                                <Package className="h-3 w-3" />
                                {order.lines.length} items
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(order.total)}</p>
                          <p className="text-sm text-muted-foreground">{order.deliveryZone}</p>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm text-muted-foreground mb-2">Items:</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {order.lines.slice(0, 6).map((line) => (
                            <div key={line.lineId} className="text-sm">
                              <span className="text-muted-foreground">{line.quantity} {line.unit}</span>
                              {' '}
                              <span>{line.itemName}</span>
                            </div>
                          ))}
                          {order.lines.length > 6 && (
                            <div className="text-sm text-muted-foreground">
                              +{order.lines.length - 6} more
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-semibold">Delivery Schedule</h1>
      <p className="text-muted-foreground">
        View upcoming deliveries organized by date
      </p>
    </div>
  );
}
